// Import Contacts helpers: parse a CSV / pasted list in the browser and guess what each column holds. Reads and writes nothing on the server.

export type ImportField = 'email' | 'full' | 'first' | 'last' | 'holder' | 'phone' | 'skip';

export const IMPORT_FIELDS: { value: ImportField; label: string }[] = [
    { value: 'email', label: 'Email address' },
    { value: 'full', label: 'Full name (split into first / last)' },
    { value: 'first', label: 'First name' },
    { value: 'last', label: 'Last name' },
    { value: 'holder', label: 'Registry holder number' },
    { value: 'phone', label: 'Phone' },
    { value: 'skip', label: 'Don’t import' },
];

export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ImportTable {
    source: string;
    // bytes, for a file
    size?: number;
    columns: string[];
    rows: string[][];
}

// RFC 4180-ish: quoted fields (with "" escapes and line breaks inside), CRLF or LF, a leading BOM; blank lines dropped
export const parseCsv = (text: string): string[][] => {
    const src = text.replace(/^﻿/, '');
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < src.length; i++) {
        const ch = src[i];
        if (quoted) {
            if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
            else if (ch === '"') quoted = false;
            else cell += ch;
            continue;
        }
        if (ch === '"' && cell === '') quoted = true;
        else if (ch === ',') { row.push(cell); cell = ''; }
        else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && src[i + 1] === '\n') i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        }
        else cell += ch;
    }
    row.push(cell);
    rows.push(row);
    return rows.map(r => r.map(c => c.trim())).filter(r => r.some(c => c !== ''));
};

// one person per line: "Name <email>" or just "email"
export const parsePasted = (text: string): ImportTable => {
    const rows = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(line => {
        const m = line.match(/^(.*?)\s*<([^>]+)>\s*$/);
        if (m) return [m[1].replace(/^["']|["']$/g, '').trim(), m[2].trim()];
        return ['', line];
    });
    return { source: 'Pasted list', columns: ['Name', 'Email'], rows };
};

const GUESSES: { field: ImportField; test: RegExp }[] = [
    { field: 'email', test: /e-?mail/ },
    { field: 'first', test: /^(first|given|fore)\s*name|^first$/ },
    { field: 'last', test: /^(last|sur|family)\s*name|^surname$|^last$/ },
    { field: 'holder', test: /holder|hin|srn|registry|investor\s*(no|number|id)/ },
    { field: 'phone', test: /phone|mobile|tel/ },
    { field: 'full', test: /name/ },
];

// header first, then the values (a column of addresses is the email even if it's called "Contact")
export const guessFields = (table: ImportTable): ImportField[] => {
    const used = new Set<ImportField>();
    const fields = table.columns.map(col => {
        const h = col.toLowerCase().trim();
        const hit = GUESSES.find(g => g.test.test(h) && !used.has(g.field));
        if (!hit) return 'skip' as ImportField;
        used.add(hit.field);
        return hit.field;
    });
    if (!used.has('email')) {
        const i = table.columns.findIndex((_, c) => fields[c] === 'skip' && table.rows.slice(0, 20).some(r => EMAIL.test(r[c] ?? '')));
        if (i >= 0) fields[i] = 'email';
    }
    return fields;
};

export interface ImportPerson {
    email: string;
    name: string;
}

// rows with a usable address, one per email (later rows merge into the first)
export const importPeople = (table: ImportTable, fields: ImportField[]) => {
    const col = (f: ImportField) => fields.indexOf(f);
    const at = (r: string[], f: ImportField) => (col(f) >= 0 ? r[col(f)] ?? '' : '').trim();
    const people = new Map<string, ImportPerson>();
    let invalid = 0;
    let duplicates = 0;
    table.rows.forEach(r => {
        const email = at(r, 'email');
        if (!EMAIL.test(email)) { invalid++; return; }
        const name = at(r, 'full') || [at(r, 'first'), at(r, 'last')].filter(Boolean).join(' ');
        const key = email.toLowerCase();
        const seen = people.get(key);
        if (seen) {
            duplicates++;
            if (!seen.name && name) seen.name = name;
            return;
        }
        people.set(key, { email, name });
    });
    return { people: Array.from(people.values()), invalid, duplicates };
};

export const templateCsv = () => [
    'First name,Last name,Email address,Phone,Registry holder number',
    'John,Citizen,john.citizen@example.com,0412 345 678,I0012345',
].join('\r\n');

export const downloadText = (name: string, text: string, type = 'text/csv') => {
    const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
};
